import { ColDef, ITooltipParams } from '@ag-grid-community/core';

import {
  AgRawMultiTooltip,
  AgRawMultiTooltipValues,
} from '@shared/components/ag-raw-tooltip/ag-raw-multi-tooltip';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { TableConstants } from '@shared/constants/table.constants';
import { decimalDivide } from '@shared/utils';
import { Utils } from '@shared/utils/utils';

import { ProtocolEntryGridContext } from '../types/protocol-entry-grid-context';
import { ProtocolEntryGridRow } from '../types/protocol-entry-grid-row';
import {
  canDiscontinuationExist,
  isTargetDateDaysOutValidForDiscontinuation,
} from '../utils/discontinuation-rate.utils';

const MIN_WIDTH = 150;

const cumulativeCellProps = (cxt: ProtocolEntryGridContext): ColDef<ProtocolEntryGridRow> => ({
  cellClass: () => {
    const styles = [TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT];

    if (cxt.editMode) {
      styles.push('!opacity-50');
    }

    return styles;
  },
  editable: false,
  headerClass: () => (cxt.editMode ? 'opacity-50' : ''),
  minWidth: MIN_WIDTH,
  valueFormatter: ({ value }) =>
    Utils.percentageFormatter(value, {
      maximumFractionDigits: 2,
    }),
});

export const getDiscontinuationColumns = (
  cxt: ProtocolEntryGridContext
): ColDef<ProtocolEntryGridRow>[] => {
  const isDiscontinuationRateEditable = cxt.editMode && !cxt.hasOverallDiscontinuationRate;

  const field = cxt.hasOverallDiscontinuationRate
    ? 'effective_discontinuation_rate'
    : 'discontinuation_rate';

  return [
    {
      cellClass: (params) => {
        const editable = isDiscontinuationRateEditable && canDiscontinuationExist(params.data!);

        const styles = [TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT];

        if (!editable && cxt.editMode) {
          styles.push('!opacity-50');
        }

        return styles;
      },
      cellClassRules: {
        'border-aux-error': (params) => !!params.data?.errors?.incorrectDiscontinuationRateError,
      },
      editable: (params) => isDiscontinuationRateEditable && canDiscontinuationExist(params.data!),
      field,
      headerClass: () => {
        const styles = [];

        if (!isDiscontinuationRateEditable && cxt.editMode) {
          styles.push('!opacity-50');
        }

        return styles;
      },
      headerName: 'Discontinuation Rate (%)',
      minWidth: MIN_WIDTH,
      tooltipComponent: AgRawMultiTooltip,
      tooltipValueGetter: (params: ITooltipParams<ProtocolEntryGridRow>) => {
        if (!isDiscontinuationRateEditable) return [];

        const tooltips: string[] = [];

        if (params.data?.optional) {
          tooltips.push('Discontinuation rates do not apply for optional visits');
        }

        if (!isTargetDateDaysOutValidForDiscontinuation(params.data!)) {
          tooltips.push('Visit Day required to add Discontinuation Rate');
        }

        if (params.data?.errors?.incorrectDiscontinuationRateError) {
          tooltips.push(MessagesConstants.PATIENT_PROTOCOL.INCORRECT_DISCONTINUATION_RATE);
        }

        return <AgRawMultiTooltipValues>tooltips.map((tooltip) => ({
          type: 'force-tooltip',
          value: tooltip,
        }));
      },
      valueFormatter: ({ value }) =>
        Utils.percentageFormatter(decimalDivide(value, 100), {
          maximumFractionDigits: 2,
        }),
    },
    {
      ...cumulativeCellProps(cxt),
      field: 'cumulative_discontinuation',
      headerName: 'Cumulative Discontinuation (%)',
      minWidth: 165,
    },
    {
      ...cumulativeCellProps(cxt),
      field: 'cumulative_retention',
      headerName: 'Cumulative Retention (%)',
    },
  ];
};
